import React, { FC, useState, useEffect } from "react";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { ListQuery } from "../../models/ListQuery";
import { ListData } from "../../models/ListData";
import { TableColumn } from "../../models/table/TableColumn";
import { SearchTerm } from "../../models/search/SearchTerm";
import { DEF_PG_SIZE_SUB_MIN } from "../../constants/maximum-page-size";
import TableTab from "../tabs/table-tab";
import { Lookup } from "../../models/Lookup";
import LookupTypes from "../../models/LookupTypes";
import CompanySearch from "../../models/search/CompanySearch";
import DepartmentSearch from "../../models/search/DepartmentSearch";
import SiteSearch from "../../models/search/SiteSearch";
import { getCompanyNamesWithPagination } from "../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../services/departmentService";
import { getSiteNamesWithPagination } from "../../services/siteService";
import { useFormikContext } from "formik";
import { getEnrolments } from "../../services/enrolmentService";
import { appendFilter } from "../../utilities/filters";
import { toast } from "react-toastify";
import { MAX_RECORDS } from "../../constants/notification-key";
import { getCategoryNameWithPagination } from "../../services/categoryService";
import CategorySearch from "../../models/search/CategorySearch";
import { WizardStepLayout } from "../wizard/wizard-summary-layout";

interface ToRecipientStepProps {
    onBack: (event: any) => void;
    onNext: (event: any) => void;
    columnDescriptors: { columns: TableColumn[]; searchColumns: SearchTerm[] };
    alwaysSelectedAndDisabled?: string[];
    setDescription: (description: string) => void;
}

const ToRecipientStep: FC<ToRecipientStepProps> = ({ onNext, onBack, columnDescriptors, alwaysSelectedAndDisabled, setDescription }) => {
    const { setFieldValue, values } = useFormikContext<any>();
    const [localLookup, setLocalLookup] = useState<Lookup[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>(values.toRecipients || []);
    const { translate } = useLocale();

    useEffect(() => {
        setLocalLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, [])

    const onLocalNext = (event: any) => {
        setFieldValue("toRecipients", selectedIds);
        setDescription(`${selectedIds.length} ${translate.byKey(TranslationKeys.selected)}`)
        onNext(event);
    }

    const onLocalBack = (event: any) => {
        onBack(event)
        setDescription("");
        setFieldValue("toRecipients", []);
    }

    const validateSelectedIds = (ids: string[]) => {
        if (ids.length >= MAX_RECORDS) {
            toast.error(translate.byKey(TranslationKeys.aMaximimumOf20RecordsCanBeSelected));
            setSelectedIds([...selectedIds])
        } else {
            setSelectedIds([...ids])
        }
    }

    return (
        <WizardStepLayout onBack={onLocalBack} onNext={(e) => onLocalNext(e)} nextDisabled={selectedIds.length === 0}>
            <div className="pl-3 flex flex-col h-full min-h-0">
                <TableContextProvider
                    dataMethod={(listParamaters?: ListQuery) =>
                        getEnrolments(translate, {
                            ...listParamaters,
                            filters: appendFilter(
                                false,
                                "Email Address",
                                "mstEmailAddress",
                                listParamaters?.filters ?? [],
                                "=isnull=",
                            ),
                        })
                    }
                    tableDescriptor={columnDescriptors.columns}
                    searchDescriptor={columnDescriptors.searchColumns}
                    useTable
                    usePagination
                    useSearch
                    useSort
                    useFilter
                    alwaysSelectedAndDisabled={values.ccRecipients}
                    maxPageSize={DEF_PG_SIZE_SUB_MIN}
                    lookup={localLookup}
                    canExportReport={false}>
                    <div className="flex-1 min-h-0">
                        <TableTab
                            title={translate.byKey(TranslationKeys.sendToRecipient)}
                            infoText={translate.byKey(TranslationKeys.recipientsEmailRequirementMessage)}
                            selectedIds={selectedIds}
                            setSelectedIds={validateSelectedIds}
                            cyName="toRecipient" />
                    </div>
                </TableContextProvider>
            </div>
        </WizardStepLayout>
    );
};

export default ToRecipientStep;
